<template>
  <div class="room-calendar-workspace">
    <aside class="room-calendar-sidebar" aria-label="Navigasi dan filter kalender">
      <section class="room-calendar-filter-card hc-no-form-icons">
        <div class="room-calendar-panel-heading">
          <div>
            <span class="room-calendar-eyebrow">Tampilan jadwal</span>
            <h3>Filter ruangan</h3>
          </div>
          <button
            v-if="hasActiveRoomFilter"
            type="button"
            class="room-calendar-reset"
            @click="resetRoomFilter"
          >
            Reset
          </button>
        </div>

        <label class="room-calendar-filter-field" for="input-room">
          <span class="room-calendar-filter-label">
            <i class="fas fa-door-open room-calendar-filter-icon" aria-hidden="true"></i>
            Ruangan
          </span>
          <treeselect
            id="input-room"
            v-model="filter.room_id"
            :multiple="false"
            :options="rooms"
            placeholder="Semua ruangan"
            @select="filterRoomOnSelect"
          />
        </label>
      </section>

      <section class="room-calendar-legend-card">
        <div class="room-calendar-panel-heading">
          <div>
            <span class="room-calendar-eyebrow">Keterangan</span>
            <h3>Status penggunaan</h3>
          </div>
        </div>

        <div class="room-calendar-status-list">
          <button
            v-for="color in statusColor"
            :key="color.id"
            type="button"
            class="room-calendar-status-chip"
            :class="`room-calendar-status-chip--${getStatusTone(color.color_class)}`"
          >
            <i :class="getStatusIconClass(color.color_class)" aria-hidden="true"></i>
            <span>{{ color.name }}</span>
          </button>
        </div>
      </section>
    </aside>

    <main class="room-calendar-main">
      <header class="room-calendar-toolbar">
        <div class="room-calendar-toolbar-title">
          <span class="room-calendar-eyebrow">Penggunaan ruangan</span>
          <h2 v-if="$refs.calendar">{{ $refs.calendar.title }}</h2>
          <h2 v-else>Kalender ruangan</h2>
        </div>

        <div class="room-calendar-toolbar-actions">
          <button type="button" class="room-calendar-today" @click="setToday">
            <i class="far fa-calendar-check" aria-hidden="true"></i>
            Hari ini
          </button>
          <div class="room-calendar-nav" aria-label="Navigasi kalender">
            <button type="button" aria-label="Sebelumnya" @click="prev">
              <i class="fas fa-chevron-left" aria-hidden="true"></i>
            </button>
            <button type="button" aria-label="Berikutnya" @click="next">
              <i class="fas fa-chevron-right" aria-hidden="true"></i>
            </button>
          </div>

          <v-menu bottom right content-class="room-calendar-view-menu">
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="room-calendar-view-button"
                v-bind="attrs"
                v-on="on"
              >
                <i class="fas fa-layer-group" aria-hidden="true"></i>
                {{ typeToLabel[type] }}
                <i class="fas fa-chevron-down room-calendar-view-chevron" aria-hidden="true"></i>
              </button>
            </template>
            <v-list dense>
              <v-list-item @click="type = 'day'">
                <v-list-item-title>Per Hari</v-list-item-title>
              </v-list-item>
              <v-list-item @click="type = 'week'">
                <v-list-item-title>Per Minggu</v-list-item-title>
              </v-list-item>
              <v-list-item @click="type = 'month'">
                <v-list-item-title>Per Bulan</v-list-item-title>
              </v-list-item>
              <v-list-item @click="type = '4day'">
                <v-list-item-title>Per 4 Hari</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </header>

      <div class="room-calendar-canvas">
          <div v-if="loadError" class="room-calendar-feedback room-calendar-feedback--error">
            <i class="fas fa-exclamation-circle"></i>
            <strong>Data ruangan belum dapat dimuat</strong>
            <span>{{ loadError }}</span>
            <button type="button" @click="getEvents">Coba lagi</button>
          </div>
          <div v-else-if="loading" class="room-calendar-feedback">
            <b-spinner small variant="primary" />
            <span>Memuat penggunaan ruangan...</span>
          </div>
          <v-calendar
            ref="calendar"
            v-model="value"
            locale="id"
            :weekdays="weekday"
            :type="type"
            :events="events"
            :event-overlap-threshold="30"
            :event-color="getEventColor"
            @click:event="showEvent"
            @click:more="viewDay"
            @click:date="viewDay"
            @change="getEvents"
          ></v-calendar>
        </div>

        <v-menu
          v-model="selectedOpen"
          :close-on-content-click="false"
          absolute
          :position-x="popoverX"
          :position-y="popoverY"
          content-class="room-calendar-event-menu"
          transition="scale-transition"
        >
          <v-card class="room-calendar-event-popover" flat>
            <div
              class="room-calendar-popover-header"
              :class="`room-calendar-popover-header--${getStatusTone(selectedEvent.color)}`"
            >
              <div>
                <span>Detail penggunaan ruangan</span>
                <h3>{{ selectedEvent.name || 'Ruangan' }}</h3>
              </div>
              <button
                type="button"
                class="room-calendar-popover-close"
                aria-label="Tutup detail"
                @click="selectedOpen = false"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            <v-card-text class="room-calendar-event-details">
              <div class="room-calendar-event-row room-calendar-event-row--wide">
                <i class="far fa-clock"></i>
                <div><span>Jadwal penggunaan</span><strong>{{ selectedEvent.details || '-' }}</strong></div>
              </div>
              <div class="room-calendar-event-row">
                <i class="fas fa-user-md"></i>
                <div><span>Dokter</span><strong>{{ selectedEvent.doctor_name || '-' }}</strong></div>
              </div>
              <div class="room-calendar-event-row">
                <i :class="getStatusIconClass(selectedEvent.color)"></i>
                <div><span>Status</span><strong>{{ selectedEvent.status_name || '-' }}</strong></div>
              </div>
              <div class="room-calendar-event-row">
                <i class="fas fa-notes-medical"></i>
                <div><span>Tindakan</span><strong>{{ selectedEvent.action_type_name || '-' }}</strong></div>
              </div>
              <div class="room-calendar-event-note">
                <span><i class="far fa-sticky-note"></i> Catatan</span>
                <p>{{ selectedEvent.notes || 'Tidak ada catatan.' }}</p>
              </div>
            </v-card-text>
          </v-card>
        </v-menu>
      </main>
    </div>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import { getUser } from '@/core/services/jwt.service.js'
import module from '@/core/modules/CrudModule.js'
import AppointmentModule from '@/core/modules/AppointmentModule.js'

export default {
  components: {
    Treeselect
  },

  data: () => ({
    filter: {
      room_id: null,
    },
    statusColor: [],
    rooms: [],
    type: 'month',
    typeToLabel: {
      month: 'Per Bulan',
      week: 'Per Minggu',
      day: 'Per Hari',
      '4day': 'Per 4 Hari',
    },
    weekday: [0, 1, 2, 3, 4, 5, 6],
    value: '',
    events: [],
    loading: false,
    loadError: '',
    colors: ['blue', 'indigo', 'deep-purple', 'cyan', 'green', 'orange', 'grey darken-1'],
    selectedEvent: {},
    selectedElement: null,
    selectedOpen: false,
    popoverX: 16,
    popoverY: 16,
    currentUser: getUser() || {}
  }),

  computed: {
    hasActiveRoomFilter() {
      return Boolean(this.filter.room_id)
    }
  },

  methods: {
    async getRooms() {
      const data = await AppointmentModule.getRoomsTreeSelect('rooms')
      this.rooms = Array.isArray(data) ? data : []
    },

    async getAppointmentStatuses() {
      const data = await AppointmentModule.getSimpleAppointmentStatuses()
      this.statusColor = Array.isArray(data) ? data : []
    },

    async getAppointmentRoom() {
      return await module.list(`appointment-rooms`, `?room_id=${this.filter.room_id || ''}`)
    },

    async filterRoomOnSelect() {
      await this.filter.room_id
      this.getEvents()
    },

    async resetRoomFilter() {
      this.filter.room_id = null
      await this.getEvents()
    },

    selectCalendarDate(date) {
      this.value = date
    },

    async getEvents() {
      this.loading = true
      this.loadError = ''
      try {
        const response = await this.getAppointmentRoom()
        const appointments = Array.isArray(response) ? response : []
        this.events = appointments.map(appointment => ({
          id: appointment.id,
          name: appointment.name || appointment.room_name || 'Ruangan pemeriksaan',
          start: new Date(appointment.start_time),
          end: new Date(appointment.end_time),
          doctor_name: appointment.doctor_name,
          notes: appointment.notes,
          status_name: appointment.appointment_status_name,
          action_type_name: appointment.action_type_name,
          color: appointment.appointment_status_color || appointment.appointment_status_col || 'var(--skin-primary)',
          timed: true,
        })).filter(event => !Number.isNaN(event.start.getTime()) && !Number.isNaN(event.end.getTime()))
      } catch (error) {
        this.events = []
        this.loadError = 'Periksa koneksi data lalu coba kembali.'
      } finally {
        this.loading = false
      }
    },

    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event
        const dayName = new Date(event.start).toLocaleDateString('id-ID', { weekday: 'long' })
        const start = new Date(event.start).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })
        const end = new Date(event.end).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })
        this.selectedEvent.details = `${event.name} Pada ${dayName} ${start} s/d ${end} dengan ${event.doctor_name}`
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight
        const popoverWidth = Math.min(380, viewportWidth - 32)
        const popoverHeight = 390
        this.popoverX = Math.min(
          Math.max(16, viewportWidth - popoverWidth - 16),
          Math.max(16, nativeEvent.clientX - popoverWidth - 18)
        )
        this.popoverY = Math.min(
          Math.max(16, viewportHeight - popoverHeight - 16),
          Math.max(16, nativeEvent.clientY - 70)
        )
        setTimeout(() => {
          this.selectedOpen = true
        }, 30)
      }
      if (this.selectedOpen) {
        this.selectedOpen = false
        setTimeout(open, 30)
      } else {
        open()
      }
      nativeEvent.stopPropagation()
    },

    viewDay({ date }) {
      this.value = date
      this.type = 'day'
    },

    setToday() {
      this.value = window.medhopToday()
    },

    prev() {
      this.$refs.calendar.prev()
    },

    next() {
      this.$refs.calendar.next()
    },

    getEventColor(event) {
      return event.color
    },

    getStatusTone(colorClass) {
      const status = String(colorClass || '').toLowerCase()
      if (!status) return 'neutral'
      if (status.includes('filed') || status.includes('primary')) return 'primary'
      if (status.includes('conf-wait') || status.includes('warning')) return 'warning'
      if (status.includes('conf') || status.includes('success')) return 'success'
      if (status.includes('handled') || status.includes('info')) return 'info'
      if (status.includes('danger') || status.includes('cancel')) return 'danger'
      return 'neutral'
    },

    getStatusIconClass(colorClass) {
      const status = String(colorClass || '').toLowerCase()
      if (!status) return 'far fa-question-circle'
      if (status.includes('filed') || status.includes('primary')) return 'far fa-paper-plane'
      if (status.includes('conf-wait') || status.includes('warning')) return 'fas fa-hourglass-half'
      if (status.includes('conf') || status.includes('success')) return 'far fa-check-circle'
      if (status.includes('handled') || status.includes('info')) return 'fas fa-check-double'
      if (status.includes('danger') || status.includes('cancel')) return 'far fa-times-circle'
      return 'far fa-question-circle'
    },
  },

  watch: {
    'filter.room_id': function (newVal) {
      if (typeof newVal === 'undefined') {
        this.filter.room_id = null
        this.filterRoomOnSelect()
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      if (this.$refs.calendar) {
        this.$refs.calendar.checkChange()
        this.$refs.calendar.scrollToTime('08:00')
      }
    })
    this.getRooms()
    this.getAppointmentStatuses()
  },
}
</script>

<style lang="scss">
@import "~vuetify/dist/vuetify.css";

.room-calendar-page.theme--light.v-application {
  --room-navy: var(--skin-deep);
  --room-blue: var(--skin-primary);
  --room-teal: var(--skin-secondary);
  --room-border: #e3eaf2;
  --room-muted: #6b7280;
  background: transparent !important;
  color: #1a2433;
  font-family: "Inter", "Poppins", sans-serif;
}

.room-calendar-page .v-application--wrap {
  min-height: 0;
}

.room-calendar-page code {
  box-shadow: none !important;
}

.room-calendar-workspace {
  display: grid;
  grid-template-columns: minmax(225px, 260px) minmax(0, 1fr);
  gap: 18px;
  align-items: start;
}

.room-calendar-sidebar {
  display: grid;
  gap: 14px;
}

.room-calendar-date-card,
.room-calendar-filter-card,
.room-calendar-legend-card,
.room-calendar-main {
  border: 1px solid var(--room-border);
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(var(--skin-deep-rgb), 0.06);
}

.room-calendar-date-card {
  padding: 16px 14px 12px;
  overflow: hidden;
  border: 0;
  background: linear-gradient(145deg, var(--room-navy) 0%, var(--room-blue) 57%, var(--room-teal) 100%);
}

.room-calendar-filter-card,
.room-calendar-legend-card {
  padding: 16px;
}

.room-calendar-panel-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.room-calendar-eyebrow {
  display: block;
  margin-bottom: 4px;
  color: var(--room-teal);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.2px;
  text-transform: uppercase;
}

.room-calendar-panel-heading h3,
.room-calendar-toolbar-title h2 {
  margin: 0 !important;
  color: #1a2433 !important;
  font-weight: 700 !important;
  line-height: 1.2;
}

.room-calendar-panel-heading h3 {
  font-size: 16px !important;
}

.room-calendar-panel-heading--inverse .room-calendar-eyebrow,
.room-calendar-panel-heading--inverse h3 {
  color: #fff !important;
}

.room-calendar-heading-icon {
  display: grid;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 16px;
}

.room-calendar-date-picker.v-picker {
  width: 100%;
  min-height: 0 !important;
  border: 0 !important;
  border-radius: 14px !important;
  background: rgba(255, 255, 255, 0.08) !important;
  box-shadow: none !important;
  transform: none !important;
}

.room-calendar-date-picker .v-picker__body {
  width: 100% !important;
  height: auto !important;
  background: transparent !important;
}

.room-calendar-date-picker .v-date-picker-header {
  padding: 6px 2px 0;
}

.room-calendar-date-picker .v-date-picker-header__value button,
.room-calendar-date-picker .v-btn,
.room-calendar-date-picker .v-date-picker-table th {
  color: #fff !important;
}

.room-calendar-date-picker .v-date-picker-table {
  height: 190px;
  padding: 0 2px 5px;
}

.room-calendar-date-picker .v-date-picker-table th {
  color: rgba(255, 255, 255, 0.65) !important;
  font-size: 10px;
}

.room-calendar-date-picker .v-date-picker-table .v-btn {
  width: 28px !important;
  height: 28px !important;
  font-size: 11px !important;
}

.room-calendar-date-picker .v-date-picker-table .v-btn--active {
  background: #fff !important;
  color: var(--room-navy) !important;
  box-shadow: 0 8px 18px rgba(10, 35, 72, 0.2);
}

.room-calendar-filter-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 7px;
  color: #293548;
  font-size: 12px;
  font-weight: 700;
}

.room-calendar-filter-icon {
  color: var(--room-teal);
  font-size: 14px;
}

.room-calendar-reset {
  border: 0;
  background: transparent;
  color: var(--room-blue);
  font-size: 12px;
  font-weight: 700;
}

.room-calendar-filter-card .vue-treeselect__control {
  height: 42px;
  border: 1px solid #dce5ef;
  border-radius: 12px;
  background: #f8fafc;
  box-shadow: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.room-calendar-filter-card .vue-treeselect--focused .vue-treeselect__control {
  border-color: rgba(var(--skin-secondary-rgb), 0.72);
  background: #fff;
  box-shadow: 0 0 0 4px rgba(var(--skin-secondary-rgb), 0.1);
}

.room-calendar-filter-card .vue-treeselect__value-container {
  padding-left: 14px;
}

.room-calendar-filter-card .vue-treeselect__placeholder,
.room-calendar-filter-card .vue-treeselect__single-value {
  color: #657185;
  font-size: 12px;
  line-height: 40px;
}

.room-calendar-filter-card input.vue-treeselect__input {
  height: 38px !important;
  min-height: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  line-height: 38px !important;
}

.room-calendar-filter-card .vue-treeselect__control-arrow-container {
  width: 36px;
}

.room-calendar-filter-card .vue-treeselect__control-arrow {
  width: 9px;
  color: var(--room-teal);
}

.room-calendar-filter-card .vue-treeselect__menu {
  margin-top: 8px;
  border: 1px solid var(--room-border);
  border-radius: 14px;
  box-shadow: 0 16px 34px rgba(var(--skin-deep-rgb), 0.14);
}

.room-calendar-status-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: stretch;
}

.room-calendar-status-chip {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 8px;
  min-height: 38px;
  padding: 8px 12px;
  border: 1px solid transparent;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 160ms ease, box-shadow 160ms ease;
}

.room-calendar-status-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.room-calendar-status-chip i {
  display: inline-block !important;
  font-size: 14px;
}

.room-calendar-status-chip span {
  display: inline-block;
}

.room-calendar-status-chip--primary { color: #007aff; background: #eaf4ff; }
.room-calendar-status-chip--warning { color: #f58220; background: #fff3e8; }
.room-calendar-status-chip--success { color: #27ae60; background: #eaf8f0; }
.room-calendar-status-chip--info { color: #7a52cd; background: #f1edfb; }
.room-calendar-status-chip--danger { color: #e74c3c; background: #fceeed; }
.room-calendar-status-chip--neutral { color: #8e9bad; background: #f4f5f8; }

.room-calendar-main {
  min-width: 0;
  overflow: hidden;
}

.room-calendar-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  min-height: 76px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--room-border);
}

.room-calendar-toolbar-title h2 {
  font-size: 20px !important;
  text-transform: capitalize;
}

.room-calendar-toolbar-actions,
.room-calendar-nav {
  display: flex;
  align-items: center;
  gap: 7px;
}

.room-calendar-today,
.room-calendar-view-button,
.room-calendar-nav button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  border: 1px solid var(--room-border);
  background: #fff;
  color: var(--room-navy);
  font-size: 12px;
  font-weight: 700;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.room-calendar-today:hover,
.room-calendar-view-button:hover,
.room-calendar-nav button:hover {
  border-color: rgba(var(--skin-secondary-rgb), 0.45);
  box-shadow: 0 8px 20px rgba(var(--skin-deep-rgb), 0.09);
  transform: translateY(-1px);
}

.room-calendar-today,
.room-calendar-view-button {
  gap: 7px;
  padding: 0 12px;
  border-radius: 10px;
}

.room-calendar-today {
  border-color: transparent;
  background: linear-gradient(135deg, var(--room-navy), var(--room-blue), var(--room-teal));
  color: #fff;
}

.room-calendar-nav {
  gap: 0;
  overflow: hidden;
  border: 1px solid var(--room-border);
  border-radius: 10px;
}

.room-calendar-nav button {
  width: 35px;
  border: 0;
  border-radius: 0;
}

.room-calendar-nav button + button {
  border-left: 1px solid var(--room-border);
}

.room-calendar-view-chevron {
  margin-left: 3px;
  font-size: 9px;
}

.room-calendar-view-menu {
  padding: 7px !important;
  border: 1px solid var(--room-border);
  border-radius: 14px !important;
  box-shadow: 0 16px 34px rgba(var(--skin-deep-rgb), 0.14) !important;
}

.room-calendar-view-menu .v-list-item {
  min-height: 40px;
  border-radius: 9px;
}

.room-calendar-canvas {
  position: relative;
  height: 560px;
  overflow: auto;
  background: #fff;
}

.room-calendar-canvas > .v-calendar {
  min-width: 680px;
}

.room-calendar-page .v-calendar-weekly__head-weekday {
  padding: 10px 6px;
  border-color: var(--room-border) !important;
  background: #f8fafc;
  color: #718096;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.room-calendar-page .v-calendar-weekly__day,
.room-calendar-page .v-calendar-daily__day,
.room-calendar-page .v-calendar-daily__day-interval {
  border-color: var(--room-border) !important;
}

.room-calendar-page .v-calendar-weekly__day:hover {
  background: #fbfdfd;
}

.room-calendar-page .v-calendar-weekly__day.v-outside {
  background: #fafbfc;
}

.room-calendar-page .v-calendar-weekly__day-label .v-btn {
  width: 30px;
  height: 30px;
  color: #4b5870;
  font-size: 12px;
  font-weight: 700;
}

.room-calendar-page .v-calendar-weekly__day.v-present .v-btn {
  background: linear-gradient(135deg, var(--room-blue), var(--room-teal)) !important;
  color: #fff !important;
  box-shadow: 0 7px 16px rgba(var(--skin-secondary-rgb), 0.24);
}

.room-calendar-page .v-event {
  min-height: 23px;
  padding: 3px 7px;
  border: 0 !important;
  border-radius: 8px !important;
  box-shadow: 0 5px 13px rgba(var(--skin-deep-rgb), 0.13);
  font-size: 11px;
  font-weight: 700;
}

.room-calendar-event-popover.v-card {
  width: min(380px, calc(100vw - 32px));
  overflow: hidden;
  border: 1px solid #e3eaf2 !important;
  border-radius: 18px !important;
  background: #fff !important;
  box-shadow: 0 24px 54px rgba(var(--skin-deep-rgb), 0.2) !important;
  transform: none !important;
}

.room-calendar-event-popover .v-toolbar {
  box-shadow: none !important;
}

.room-calendar-popover-header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 17px 18px 15px 21px;
  border-bottom: 1px solid #e7edf4;
  background: linear-gradient(135deg, #f8fafc 0%, #fff 100%);
}

.room-calendar-popover-header::before {
  position: absolute;
  inset: 0 auto 0 0;
  width: 5px;
  background: #356fd0;
  content: "";
}

.room-calendar-popover-header--success::before { background: var(--skin-secondary); }
.room-calendar-popover-header--warning::before { background: #e3a326; }
.room-calendar-popover-header--danger::before { background: #d65353; }
.room-calendar-popover-header--info::before { background: #278ab0; }
.room-calendar-popover-header--neutral::before { background: #718096; }

.room-calendar-popover-header > div {
  min-width: 0;
}

.room-calendar-popover-header span {
  display: block;
  margin-bottom: 3px;
  color: #8090a2;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.room-calendar-popover-header h3 {
  margin: 0;
  color: #1e293b;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.room-calendar-event-popover .v-toolbar__title {
  font-size: 16px;
  font-weight: 700;
}

.room-calendar-popover-close {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border: 0;
  border-radius: 10px;
  background: #eef2f6;
  color: #6e7e90;
}

.room-calendar-event-details {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 16px;
  padding: 17px 18px 18px !important;
}

.room-calendar-event-row {
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 9px;
}

.room-calendar-event-row--wide,
.room-calendar-event-note {
  grid-column: 1 / -1;
}

.room-calendar-event-row--wide {
  padding-bottom: 12px;
  border-bottom: 1px solid #edf1f5;
}

.room-calendar-event-row > i {
  width: 29px;
  height: 29px;
  flex: 0 0 29px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  background: #eff7f6;
  color: var(--skin-secondary);
  font-size: 11px;
}

.room-calendar-event-row div {
  min-width: 0;
}

.room-calendar-event-row span,
.room-calendar-event-row strong {
  display: block;
}

.room-calendar-event-row span {
  margin-bottom: 2px;
  color: #8a99aa;
  font-size: 10px;
  font-weight: 700;
}

.room-calendar-event-row strong {
  color: #344256;
  font-size: 12px;
  font-weight: 750;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.room-calendar-event-note {
  padding: 11px 12px;
  border: 1px solid #e9eef3;
  border-radius: 10px;
  background: #f9fbfc;
}

.room-calendar-event-note span {
  display: block;
  margin-bottom: 5px;
  color: #7b8b9d;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
}

.room-calendar-event-note span i {
  margin-right: 5px;
  color: var(--skin-secondary);
}

.room-calendar-event-note p {
  margin: 0;
  color: #526174;
  font-size: 11px;
  line-height: 1.55;
  overflow-wrap: anywhere;
}

.room-calendar-feedback {
  position: absolute;
  z-index: 4;
  top: 50%;
  left: 50%;
  min-width: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 17px;
  transform: translate(-50%, -50%);
  border: 1px solid #e3eaf2;
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 14px 34px rgba(var(--skin-deep-rgb), 0.12);
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
}

.room-calendar-feedback--error {
  flex-direction: column;
  text-align: center;
}

.room-calendar-feedback--error > i { color: #c44545; font-size: 20px; }
.room-calendar-feedback--error strong { color: #7f3030; }
.room-calendar-feedback--error span { color: #947171; font-size: 11px; }
.room-calendar-feedback--error button {
  padding: 7px 13px;
  border: 0;
  border-radius: 8px;
  background: var(--skin-primary);
  color: #fff;
  font-size: 11px;
  font-weight: 750;
}

.room-calendar-event-details p {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 12px;
  color: #5d687a;
  font-size: 13px;
  line-height: 1.55;
}

.room-calendar-event-details p:last-child {
  margin-bottom: 0;
}

.room-calendar-event-details i {
  width: 17px;
  margin-top: 3px;
  color: var(--room-teal);
  text-align: center;
}

@media (max-width: 1199px) {
  .room-calendar-workspace {
    grid-template-columns: 230px minmax(0, 1fr);
    gap: 14px;
  }

  .room-calendar-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }
}

@media (max-width: 991px) {
  .room-calendar-workspace {
    grid-template-columns: 1fr;
  }

  .room-calendar-sidebar {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .room-calendar-date-card {
    grid-row: span 2;
  }
}

@media (max-width: 767px) {
  .room-calendar-sidebar {
    grid-template-columns: 1fr;
  }

  .room-calendar-date-card {
    grid-row: auto;
  }

  .room-calendar-toolbar,
  .room-calendar-filter-card,
  .room-calendar-legend-card {
    padding: 15px;
  }

  .room-calendar-toolbar-actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .room-calendar-today,
  .room-calendar-view-button {
    flex: 1 1 auto;
  }

  .room-calendar-event-popover.v-card {
    min-width: min(350px, calc(100vw - 32px)) !important;
  }

  .room-calendar-event-details {
    grid-template-columns: 1fr;
  }

  .room-calendar-event-row--wide,
  .room-calendar-event-note {
    grid-column: auto;
  }
}
</style>
